export function validateStagedWorksheetConfig(config = {}) {
	let stages = config.stages || []
	if (!config.title || !Array.isArray(stages) || (!stages.length && !config.orderedAcrossStages && !config.contextMap && !config.linkedRequirements)) {
		throw new Error("Invalid staged worksheet configuration")
	}
	if (config.linkedRequirements !== undefined) {
		const matching = config.linkedRequirements
		if (!matching || !Array.isArray(matching.groupingOptions) || !matching.groupingOptions.every(row => row?.id && row?.title && ["dimension", "context", "accessNeed"].includes(row.mode))) throw new Error("Invalid linked requirement configuration")
		const ids = matching.groupingOptions.map(row => row.id)
		if (new Set(ids).size !== ids.length || (matching.groupingId && !ids.includes(matching.groupingId))) throw new Error("Invalid linked requirement grouping")
	}
	for (let stage of stages) {
		if (!stage.id || !stage.title) {
			throw new Error("Invalid staged worksheet stage")
		}
		if (stage.categories !== undefined) {
			if (!Array.isArray(stage.categories)) throw new Error("Invalid worksheet categories")
			let categoryIds = new Set()
			for (let category of stage.categories) {
				if (!category?.id || !category?.title || categoryIds.has(category.id)) throw new Error("Invalid worksheet category")
				categoryIds.add(category.id)
			}
		}
	}
	if (config.contextMap !== undefined) {
		const map = config.contextMap
		if (!map || !Array.isArray(map.statusOptions) || !Array.isArray(map.prompts)) throw new Error("Invalid contextual map configuration")
		for (const [rows, idKey, labelKey] of [[map.statusOptions, "value", "label"], [map.prompts, "id", "text"]]) {
			const ids = new Set()
			for (const row of rows) {
				if (!row?.[idKey] || !row?.[labelKey] || ids.has(row[idKey])) throw new Error("Invalid contextual map option")
				ids.add(row[idKey])
			}
		}
	}
	if (config.ratingsEnabled !== undefined && (!Number.isInteger(Number(config.ratingScaleMax)) || Number(config.ratingScaleMax) < 3 || Number(config.ratingScaleMax) > 100)) {
		throw new Error("Invalid graded plan rating scale")
	}
	for (let [key, idKey, titleKey] of [["entryFields", "id", "title"], ["choiceOptions", "value", "label"], ["periodOptions", "value", "label"]]) {
		if (config[key] === undefined) continue
		if (!Array.isArray(config[key])) throw new Error(`Invalid staged worksheet ${key}`)
		let ids = new Set()
		for (let row of config[key]) {
			if (!row?.[idKey] || !row?.[titleKey] || ids.has(row[idKey])) throw new Error(`Invalid staged worksheet ${key}`)
			ids.add(row[idKey])
		}
	}
}

const WORKSHEET_STRUCTURAL_VALUE_IDS = new Set(["categoryIds", "isUncertain", "isComplete", "isAttempted"])

export function validateStagedWorksheetInput(payload = {}, config = {}) {
	const worksheet = payload.stagedWorksheet
	if (worksheet === undefined) return
	if (!worksheet || typeof worksheet !== "object" || Array.isArray(worksheet)) throw new Error("Invalid staged worksheet payload")
	for (const key of ["stages", "archivedStages"]) {
		if (worksheet[key] !== undefined && !Array.isArray(worksheet[key])) throw new Error("Invalid staged worksheet payload")
	}
	for (const stage of [...(worksheet.stages || []), ...(worksheet.archivedStages || [])]) {
		if (!stage || (stage.items !== undefined && !Array.isArray(stage.items)) || !Array.isArray(stage.alternatives || [])) throw new Error("Invalid staged worksheet stage payload")
		const configStage = (config.stages || []).find(row => row.id === stage.id) || {}
		for (const item of stage.items || []) validateStagedWorksheetItem(item, config, configStage, true)
	}
	if (config.contextMap) validateContextMapPayload(worksheet, config)
	if (config.linkedRequirements) validateLinkedRequirementPayload(worksheet)
}

export function validateStagedWorksheetPayload(enginePayload = {}, config = {}) {
	const worksheet = enginePayload.stagedWorksheet
	if (!worksheet || !Array.isArray(worksheet.stages)) throw new Error("Invalid staged worksheet payload")
	validateStagedWorksheetConfig(config)
	const configStages = new Map((config.stages || []).map(stage => [stage.id, stage]))
	const globalFields = createGlobalFieldReferences(config, worksheet)
	for (const stage of worksheet.stages) {
		if (!stage?.id || (configStages.size && !configStages.has(stage.id)) || !Array.isArray(stage.items) || !Array.isArray(stage.alternatives || [])) {
			throw new Error("Invalid staged worksheet stage payload")
		}
		const configStage = configStages.get(stage.id) || {}
		for (const item of stage.items) {
			validateStagedWorksheetItem(item, config, configStage)
			if (Array.isArray(config.entryFields)) validateGlobalEntryValues(item.values, configStage, globalFields)
		}
	}
	if (config.contextMap) validateContextMapPayload(worksheet, config)
	if (config.linkedRequirements) validateLinkedRequirementPayload(worksheet)
}

function validateLinkedRequirementPayload(worksheet) {
	const matching = worksheet.linkedRequirements || {}
	if (matching.needs !== undefined && (!Array.isArray(matching.needs) || matching.needs.some(row => !row?.id || typeof row.text !== "string" || typeof row.evidence !== "string" || typeof row.context !== "string"))) throw new Error("Invalid documented need")
	if (matching.consideredDimensionIds !== undefined && (!Array.isArray(matching.consideredDimensionIds) || matching.consideredDimensionIds.some(id => typeof id !== "string"))) throw new Error("Invalid dimension coverage")
	if (matching.unresolvedQuestion !== undefined && typeof matching.unresolvedQuestion !== "string") throw new Error("Invalid unresolved question")
	for (const stage of worksheet.stages || []) for (const item of stage.items || []) {
		const values = item.values || {}
		if (values.needIds !== undefined && (!Array.isArray(values.needIds) || values.needIds.some(id => typeof id !== "string"))) throw new Error("Invalid requirement references")
		for (const key of ["rationale", "context", "unresolvedQuestion"]) if (values[key] !== undefined && typeof values[key] !== "string") throw new Error("Invalid requirement detail")
	}
}

function validateContextMapPayload(worksheet, config) {
	if (worksheet.context !== undefined && typeof worksheet.context !== "string") throw new Error("Invalid contextual map focus")
	if (worksheet.alternatives !== undefined && (!Array.isArray(worksheet.alternatives) || worksheet.alternatives.some(row => !row?.id || typeof row.text !== "string"))) {
		throw new Error("Invalid contextual map alternatives")
	}
	if (worksheet.promptAnswers !== undefined && (worksheet.promptAnswers === null || typeof worksheet.promptAnswers !== "object" || Array.isArray(worksheet.promptAnswers) || Object.values(worksheet.promptAnswers).some(value => typeof value !== "string"))) {
		throw new Error("Invalid contextual map answers")
	}
	const statusIds = new Set(config.contextMap.statusOptions.map(option => option.value))
	for (const stage of worksheet.stages || []) {
		const configuredStage = (config.stages || []).find(row => row.id === stage.id)
		for (const item of stage.items || []) {
			const statusId = item.values?.statusId
			if (statusId !== undefined && statusId !== "" && !statusIds.has(statusId) && !item.values?.statusLabel) throw new Error("Invalid contextual link status")
			for (const field of configuredStage?.itemFields || []) {
				const value = item.values?.[field.id]
				if (value !== undefined && value !== null && typeof value !== "string") throw new Error("Invalid contextual entry field")
			}
		}
	}
}

function validateStagedWorksheetItem(item, config, configStage, allowMissing = false) {
	if (!item || (!allowMissing && !item.id) || ((!allowMissing || item.text !== undefined) && typeof item.text !== "string")) {
		throw new Error("Invalid staged worksheet item payload")
	}
	if (config.gradedPlan && (
		(!allowMissing || item.notes !== undefined) && typeof item.notes !== "string" ||
		(!allowMissing || item.history !== undefined) && !Array.isArray(item.history)
	)) throw new Error("Invalid graded plan step")
	const values = item.values
	if (config.ratingsEnabled === true && values?.rating !== undefined && values?.rating !== null && values?.rating !== "") {
		const input = values.rating
		const rating = Number(input)
		if ((typeof input !== "number" && (typeof input !== "string" || !input.trim())) || !Number.isInteger(rating) || rating < 0 || rating > Number(config.ratingScaleMax)) throw new Error("Rating is outside the configured range")
	}
	if ((values !== undefined || (!allowMissing && Array.isArray(config.entryFields))) && (values == null || typeof values !== "object" || Array.isArray(values))) {
		throw new Error("Invalid staged worksheet entry values")
	}
	validateWorksheetCapabilities(values || {}, configStage)
	if (Array.isArray(config.entryFields) && values?.choices !== undefined && (!Array.isArray(values.choices) || values.choices.some(id => typeof id !== "string"))) {
		throw new Error("Invalid staged worksheet entry values")
	}
}

function validateWorksheetCapabilities(values, configStage) {
	if ([values.isComplete, values.isAttempted].some(value => value !== undefined && typeof value !== "boolean")) {
		throw new Error("Invalid worksheet completion state")
	}
	if (configStage.categories !== undefined || values.categoryIds !== undefined || values.isUncertain !== undefined) {
		if (values.categoryIds !== undefined && (!Array.isArray(values.categoryIds) || values.categoryIds.some(id => typeof id !== "string"))) {
			throw new Error("Invalid worksheet category selection")
		}
		if (values.isUncertain !== undefined && typeof values.isUncertain !== "boolean") throw new Error("Invalid worksheet uncertainty")
	}
}

function createGlobalFieldReferences(config, worksheet) {
	return {
		fieldIds: new Set([...(config.entryFields || []), ...(worksheet.entryFieldSnapshot || [])].map(field => field.id)),
		choiceIds: new Set([...(config.choiceOptions || []), ...(worksheet.choiceOptionSnapshot || [])].map(option => option.value)),
		periodIds: new Set([...(config.periodOptions || []), ...(worksheet.periodOptionSnapshot || [])].map(option => option.value)),
	}
}

function validateGlobalEntryValues(values, configStage, references) {
	const stageFieldIds = new Set((configStage.itemFields || []).map(field => field.id))
	const isStageValue = key => stageFieldIds.has(key) || WORKSHEET_STRUCTURAL_VALUE_IDS.has(key)
	const hasUnknownField = Object.keys(values).some(key => !["period", "choices"].includes(key) && !references.fieldIds.has(key) && !isStageValue(key))
	const hasInvalidGlobalValue = Object.entries(values).some(([key, value]) => references.fieldIds.has(key) && !isStageValue(key) && value != null && typeof value !== "string")
	if (hasUnknownField || hasInvalidGlobalValue || (values.period && !references.periodIds.has(values.period)) || (values.choices || []).some(id => !references.choiceIds.has(id))) {
		throw new Error("Invalid staged worksheet entry selection")
	}
}
